/// <amd-module name="typeahead/core/EssSuggestion"/>

import {
    IEssId, IEssSuggestionResult, IExtraSearchParam, IFreetextConcepts, Suggestion,
} from "typeahead/core/Suggestion";
import {Optional} from "typeahead/core/util/Optional";

// EssSuggestion class upon instantiation populates the underlying data based on ESS response
export class EssSuggestion extends Suggestion {
    protected data: IEssSuggestionResult;

    public getSourceName(): Optional<string> {
        return Optional.ofNullable(this.getEssId().isPresent() ? this.getEssId().get().sourceName : undefined);
    }

    public getSourceId(): Optional<string> {
        return Optional.ofNullable(this.getEssId().isPresent() ? this.getEssId().get().sourceId : undefined);
    }

    public getRegionId(): Optional<string> {
        return Optional.ofNullable(this.data.gaiaId);
    }

    public getTla(): Optional<string> {
        return Optional.ofNullable(this.data.hierarchyInfo && this.data.hierarchyInfo.airport ?
            this.data.hierarchyInfo.airport.airportCode : undefined);
    }

    public getSuggestionHierarchyType(): Optional<string> {
        return Optional.of(this.data.hierarchyInfo ?
            (this.data.hierarchyInfo.isChild === "true" ? "child" : "parent") : "parent");
    }

    public getRelationInfo(): Optional<string> {
        return Optional.of(this.data.hierarchyInfo && this.data.hierarchyInfo.relation &&
        this.data.hierarchyInfo.relation.length > 0 ? this.data.hierarchyInfo.relation[0] : "-");
    }

    public getHotelId(): Optional<string> {
        return Optional.ofNullable(this.getEssId().isPresent() && this.getEssId().get().sourceName === "LCM" ?
            this.getEssId().get().sourceId : undefined);
    }

    public getTypeId(): Optional<string> {
        return Optional.of(this.data.essId && this.data.essId.sourceId ? this.data.essId.sourceId : "-");
    }

    public getCity(): Optional<string> {
        return Optional.ofNullable(this.data.hotelAddress ? this.data.hotelAddress.city : undefined);
    }

    public getMultiCity(): Optional<string> {
        return Optional.of(this.data.hierarchyInfo.airport ?
            this.data.hierarchyInfo.airport.multicity : this.data.hierarchyInfo.multicity);
    }

    public getSubType(): Optional<string> {
        return Optional.ofNullable(this.data.subtype);
    }

    public getStationCode(): Optional<string> {
        return Optional.ofNullable(this.data.hierarchyInfo && this.data.hierarchyInfo.rails ?
            this.data.hierarchyInfo.rails.stationCode : undefined);
    }

    public getPointOfSupply(): Optional<string> {
        return Optional.ofNullable(this.data.hierarchyInfo && this.data.hierarchyInfo.rails ?
            this.data.hierarchyInfo.rails.pointOfSupply : undefined);
    }

    public getCountryName(): Optional<string> {
        return Optional.ofNullable(this.data.hierarchyInfo && this.data.hierarchyInfo.country ?
            this.data.hierarchyInfo.country.name : undefined);
    }

    public getCountryIsoCode2(): Optional<string> {
        return Optional.ofNullable(this.data.hierarchyInfo && this.data.hierarchyInfo.country ?
            this.data.hierarchyInfo.country.isoCode2 : undefined);
    }

    public getCountryIsoCode3(): Optional<string> {
        return Optional.ofNullable(this.data.hierarchyInfo && this.data.hierarchyInfo.country ?
            this.data.hierarchyInfo.country.isoCode3 : undefined);
    }

    /* returns an Optional containing either:
     * -- "true" if the selected suggestion is a HEDS (Hotel Expedia Doesn't Sell)
     * -- empty for all other selected suggestions
     * if "true", HEDS=true will be sent to HSRP
     * if empty, no query parameter will be sent
     */
    public getIsHeds(): Optional<string> {
        return this.getEssId().isPresent() && this.getEssId().get().sourceName === "TREX" ?
            Optional.ofNullable("true") :
            Optional.empty<string>();
    }

    public shouldTriggerDomElementChangeOnSelection(): boolean {
        return true;
    }

    public getExtraSearchParams(): Optional<IExtraSearchParam[]> {
        let extraSearchParams: IExtraSearchParam[] = [];
        [].push.apply(extraSearchParams, this.data.extraSearchParams);
        if (this.getHotelFilterParams()) { extraSearchParams.push(this.getHotelFilterParams()); }
        return Optional.ofNullable(extraSearchParams.length ? extraSearchParams : undefined);
    }

    // This is a hack for experiment 15289 (Redirect Cuba Pkg Search) and will be removed in future.
    // Populate the redirect path based on ESS response
    public getRedirectPath(): Optional<string> {
        return Optional.ofNullable(this.data.regionMetadata ? this.data.regionMetadata.redirect : undefined);
    }

    // Populate the concepts for free text suggestion based on ESS response
    public getConcepts(): Optional<IFreetextConcepts> {
        return Optional.ofNullable(this.data.concepts ? this.data.concepts : undefined);
    }

    private getHotelFilterParams(): IExtraSearchParam {
        let hotelFilterId: string = (this.data.hierarchyInfo && this.data.hierarchyInfo.hotels) ?
            this.data.hierarchyInfo.hotels.hotelFilterId : undefined;
        if (hotelFilterId) {
            let amenityId: string = hotelFilterId.replace("AM", "");
            if (amenityId) {
                return {field: "amenities", value: amenityId};
            }
        }
        return undefined;
    }

    private getEssId(): Optional<IEssId> {
        return Optional.ofNullable(this.data.essId);
    }
}
